import PortOne from '@portone/browser-sdk/v2'
import {
  IAMPORT_SDK_URL,
  PORTONE_API_VERSION,
  PORTONE_PAY_METHOD,
  PORTONE_V1_PAY_METHOD,
} from '@/lib/PortOne/portOne.constants'
import { getVirtualAccountDueDate } from '@/lib/PortOne/portOne.utils'
import type {
  IIamport,
  IPortOnePaymentResult,
  IPortOneRequestPayParams,
} from '@/lib/PortOne/portOne.types'

/** PortOne V1(iamport) SDK 로드 — 이미 로드되어 있으면 즉시 resolve */
const loadIamportSdk = () =>
  new Promise<IIamport>((resolve, reject) => {
    if (window.IMP) return resolve(window.IMP as unknown as IIamport)

    const onError = () => reject(new Error('결제 모듈을 불러오지 못했습니다.'))
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${IAMPORT_SDK_URL}"]`)
    const script = existing ?? document.createElement('script')
    script.addEventListener('load', () =>
      window.IMP ? resolve(window.IMP as unknown as IIamport) : onError()
    )
    script.addEventListener('error', onError)
    if (!existing) {
      script.src = IAMPORT_SDK_URL
      script.async = true
      document.head.appendChild(script)
    }
  })

/**
 * PortOne V1 결제창 (IMP.request_pay)
 * merchant_uid 는 prepare 의 paymentId — complete/웹훅 모두 이 값으로 결제 이력을 찾는다.
 */
const requestPortOnePayV1 = async ({
  prepared,
  payMethod,
  buyer,
  customData,
  redirectUrl,
  digital,
}: IPortOneRequestPayParams): Promise<IPortOnePaymentResult> => {
  if (!prepared.impCode || !prepared.paymentId) {
    throw new Error('결제 계정 정보(고객사 식별코드)가 등록되지 않았습니다.')
  }

  const IMP = await loadIamportSdk()
  IMP.init(prepared.impCode)

  return new Promise(resolve => {
    IMP.request_pay(
      {
        channelKey: prepared.channelKey,
        pay_method: PORTONE_V1_PAY_METHOD[payMethod],
        merchant_uid: prepared.paymentId,
        name: prepared.orderName,
        amount: prepared.totalAmount,
        buyer_name: buyer.name,
        buyer_tel: buyer.phoneNumber,
        buyer_email: buyer.email,
        custom_data: customData,
        digital,
        notice_url: prepared.noticeUrl || undefined,
        m_redirect_url: redirectUrl,
        ...(payMethod === PORTONE_PAY_METHOD.VIRTUAL_ACCOUNT && {
          vbank_due: getVirtualAccountDueDate(),
        }),
      },
      response => {
        const isFailed = !response || response.error_code != null || !response.imp_uid
        // SDK가 merchant_uid를 자체 발급하면 서버가 prepare paymentId로 결제를 찾지 못함
        if (!isFailed && response.merchant_uid !== prepared.paymentId) {
          console.error('[결제] merchant_uid 불일치', {
            expected: prepared.paymentId,
            received: response.merchant_uid,
            impUid: response.imp_uid,
          })
        }
        resolve({
          paymentId: prepared.paymentId,
          code: isFailed ? response?.error_code || 'FAILED' : null,
          message: response?.error_msg,
        })
      }
    )
  })
}

/**
 * PortOne V2 결제창 (PortOne.requestPayment)
 * @returns 창을 닫은 경우 null
 */
const requestPortOnePayV2 = async ({
  prepared,
  payMethod,
  buyer,
  customData,
  redirectUrl,
}: IPortOneRequestPayParams): Promise<IPortOnePaymentResult | null> => {
  if (!prepared.storeId || !prepared.paymentId) {
    throw new Error('결제 계정 정보(상점 아이디)가 등록되지 않았습니다.')
  }

  const response = await PortOne.requestPayment({
    storeId: prepared.storeId,
    channelKey: prepared.channelKey,
    paymentId: prepared.paymentId,
    orderName: prepared.orderName,
    totalAmount: prepared.totalAmount,
    currency: 'KRW',
    payMethod,
    customer: {
      fullName: buyer.name,
      phoneNumber: buyer.phoneNumber,
      email: buyer.email,
    },
    ...(payMethod === PORTONE_PAY_METHOD.VIRTUAL_ACCOUNT && {
      virtualAccount: { accountExpiry: { dueDate: getVirtualAccountDueDate() } },
    }),
    customData,
    redirectUrl,
  })
  if (!response) return null

  return { paymentId: response.paymentId, code: response.code ?? null, message: response.message }
}

/** prepare 응답의 apiVersion 으로 SDK 분기 (V1: IMP.request_pay / V2: PortOne.requestPayment) */
export const requestPortOnePay = (params: IPortOneRequestPayParams) =>
  params.prepared.apiVersion === PORTONE_API_VERSION.V1
    ? requestPortOnePayV1(params)
    : requestPortOnePayV2(params)
